import React, { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { COMPANY } from "@/data/site";
import { api } from "@/lib/api";
import { Mail, MapPin, Phone, MessageCircle, ExternalLink, Send, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { QRCodeSVG } from "qrcode.react";
import { waLink } from "@/lib/whatsapp";
import { trackEvent } from "@/lib/analytics";

const PRODUCT_OPTIONS = [
  "LFP Battery",
  "Solar Street Light",
  "Custom Pack",
  "Inverter",
  "EV Battery",
  "Battery Service",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    quantity: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waHref, setWaHref] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    const composedMessage =
      `${form.message}` +
      (form.quantity ? `\nQuantity required: ${form.quantity}` : "");
    const payload = { ...form, message: composedMessage };
    delete payload.quantity;
    try {
      await api.post("/enquiries", payload);
      trackEvent("generate_lead", {
        form: "contact",
        product: form.service || "unspecified",
        quantity: form.quantity || null,
      });
      const waMsg =
        `Hello VPS, I just submitted an enquiry.\nName: ${form.name}` +
        (form.service ? `\nProduct: ${form.service}` : "") +
        (form.quantity ? `\nQuantity: ${form.quantity}` : "") +
        `\nMessage: ${form.message}`;
      setWaHref(waLink(waMsg));
      setSuccess(true);
      toast.success("Enquiry received. Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", quantity: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send. Please try WhatsApp or call us."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        path="/contact"
        title="Contact Us"
        description="Talk to VOLTECH POWER SOLUTIONS — sales 9398422625, service 7672067048, info@voltechpowersolutions.in. Visit us at BN Reddy Nagar, Cherlapally, Hyderabad — 500051."
      />
      <PageHeader
        eyebrow="Contact"
        title="Let's build your project."
        subtitle="Share your requirement — battery sizing, solar EPC scope, EV order or CSR partnership — and our team will revert with a tailored proposal."
        testId="contact-page-header"
      />

      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="industrial-card p-6 md:p-10">
              <div className="eyebrow">Send enquiry</div>
              <h2 className="font-display text-2xl md:text-3xl uppercase mt-3 tracking-tighter">
                Tell us what you need.
              </h2>

              {success ? (
                <div
                  className="mt-8 border border-[#25d366]/40 bg-[#25d366]/5 p-6"
                  data-testid="contact-success-banner"
                >
                  <Check size={24} className="text-[#25d366]" />
                  <div className="font-display text-lg uppercase mt-3">
                    Enquiry received
                  </div>
                  <p className="text-sm text-zinc-300 mt-2">
                    Our team will respond within 24 working hours. For urgent
                    requirements, WhatsApp or call us directly.
                  </p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    {waHref && (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                        data-testid="contact-success-whatsapp"
                      >
                        <MessageCircle size={14} /> Continue on WhatsApp
                      </a>
                    )}
                    <button
                      onClick={() => setSuccess(false)}
                      className="btn-secondary"
                      data-testid="contact-send-another-btn"
                    >
                      Send another enquiry
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
                  <div>
                    <label className="label-mono">Name *</label>
                    <input
                      className="vps-input"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your full name"
                      required
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="label-mono">Email *</label>
                    <input
                      className="vps-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@company.com"
                      required
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label className="label-mono">Phone *</label>
                    <input
                      className="vps-input"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+91 9XXXXXXXXX"
                      required
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="label-mono">Company / Org</label>
                    <input
                      className="vps-input"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Optional"
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label className="label-mono">Product interested in</label>
                    <select
                      className="vps-input"
                      value={form.service}
                      onChange={(e) => update("service", e.target.value)}
                      data-testid="contact-input-service"
                    >
                      <option value="">Select a product…</option>
                      {PRODUCT_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono">Quantity required</label>
                    <input
                      className="vps-input"
                      value={form.quantity}
                      onChange={(e) => update("quantity", e.target.value)}
                      placeholder="e.g. 10 units / 1 pack"
                      data-testid="contact-input-quantity"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-mono">Message *</label>
                    <textarea
                      className="vps-input"
                      rows={5}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Describe your requirement — voltage, capacity, quantity, timeline…"
                      required
                      data-testid="contact-input-message"
                    />
                  </div>
                  <div className="md:col-span-2 flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="contact-submit-btn"
                    >
                      <Send size={14} /> {submitting ? "Sending…" : "Send Enquiry"}
                    </button>
                    <a
                      href={`https://wa.me/${COMPANY.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                      data-testid="contact-whatsapp-btn"
                    >
                      <MessageCircle size={14} /> WhatsApp instead
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* CONTACT CARDS */}
          <div className="lg:col-span-5 space-y-4">
            <div className="industrial-card p-6" data-testid="contact-response-times">
              <Clock size={20} className="text-[#e63946]" />
              <div className="eyebrow mt-3">Response commitment</div>
              <ul className="mt-3 space-y-2 text-sm text-zinc-300">
                <li className="flex items-start gap-2"><Check size={13} className="text-[#25d366] mt-1" /> WhatsApp: within 30 minutes (9 AM–7 PM)</li>
                <li className="flex items-start gap-2"><Check size={13} className="text-[#25d366] mt-1" /> Form submission: within 2 hours</li>
                <li className="flex items-start gap-2"><Check size={13} className="text-[#25d366] mt-1" /> Bulk order quotes: within 24 hours</li>
              </ul>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Mon–Sat 9:00–19:00 · Sun: WhatsApp only
              </div>
            </div>

            <div className="industrial-card p-6" data-testid="contact-info-address">
              <MapPin size={20} className="text-[#e63946]" />
              <div className="eyebrow mt-3">Visit</div>
              <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                {COMPANY.address}
              </p>
              <a
                href={COMPANY.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#e63946]"
                data-testid="contact-maps-link"
              >
                Open in Google Maps <ExternalLink size={11} />
              </a>
            </div>

            <div className="industrial-card p-6" data-testid="contact-info-call">
              <Phone size={20} className="text-[#e63946]" />
              <div className="eyebrow mt-3">Call</div>
              <div className="mt-3 space-y-2">
                <a href={`tel:${COMPANY.phoneSales}`} className="block text-sm text-zinc-300 hover:text-[#e63946]" data-testid="contact-tel-sales">
                  Sales · {COMPANY.phoneSales}
                </a>
                <a href={`tel:${COMPANY.phoneService}`} className="block text-sm text-zinc-300 hover:text-[#e63946]" data-testid="contact-tel-service">
                  Service · {COMPANY.phoneService}
                </a>
              </div>
            </div>

            <div className="industrial-card p-6" data-testid="contact-info-email">
              <Mail size={20} className="text-[#e63946]" />
              <div className="eyebrow mt-3">Email</div>
              <a
                href={`mailto:${COMPANY.email}`}
                className="mt-3 block text-sm text-zinc-300 hover:text-[#e63946] break-all"
                data-testid="contact-mail-link"
              >
                {COMPANY.email}
              </a>
            </div>

            <div className="industrial-card p-6" data-testid="contact-info-extras">
              <MessageCircle size={20} className="text-[#25d366]" />
              <div className="eyebrow mt-3">Channels</div>
              <div className="mt-3 space-y-2 text-sm text-zinc-300">
                <a
                  href={`https://wa.me/${COMPANY.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#25d366]"
                  data-testid="contact-wa-direct"
                >
                  <ExternalLink size={12} /> WhatsApp chat
                </a>
                <a
                  href={COMPANY.whatsappChannel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#25d366]"
                  data-testid="contact-wa-channel"
                >
                  <ExternalLink size={12} /> WhatsApp channel
                </a>
                <a
                  href={COMPANY.indiamart}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#e63946]"
                  data-testid="contact-indiamart"
                >
                  <ExternalLink size={12} /> IndiaMART storefront
                </a>
              </div>
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="eyebrow mb-3">Our mobile app</div>
                <div className="flex items-center gap-4 flex-wrap">
                  <GooglePlayBadge testId="contact-google-play" />
                  <div className="text-center" data-testid="contact-app-qr">
                    <div className="bg-white p-2 inline-block">
                      <QRCodeSVG
                        value={COMPANY.playStore}
                        size={84}
                        level="M"
                        fgColor="#0a0a0c"
                        bgColor="#ffffff"
                      />
                    </div>
                    <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                      Scan to install
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="border-b border-white/10" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="eyebrow mb-4">Location</div>
          <div className="industrial-card overflow-hidden">
            <iframe
              title="VPS Hyderabad location"
              src={COMPANY.mapsEmbed}
              className="w-full h-[420px] grayscale-[20%] contrast-110"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
